import { appendQualitySlide } from './reportQualityExport';
// Generate PowerPoint report for BPCM Assessment
import pptxgen from 'pptxgenjs';

export const generateBPCMPptx = (results, fullResults) => {
  const pptx = new pptxgen();
  
  pptx.author = 'SAMANVAI';
  pptx.title = `BPCM Assessment - ${results.company_name}`;
  pptx.subject = 'Business Process Change Management Assessment';
  
  // Color scheme - Blue/Purple professional theme
  const colors = {
    primary: '3B82F6',
    secondary: '8B5CF6',
    accent: '6366F1',
    dark: '1E293B',
    darkGray: '374151',
    mediumGray: '6B7280',
    lightGray: 'F3F4F6',
    white: 'FFFFFF',
    red: 'EF4444',
    amber: 'F59E0B',
    green: '10B981'
  };
  
  const getScoreColor = (score) => {
    if (score < 2.0) return colors.red;
    if (score < 3.0) return colors.amber;
    if (score < 4.0) return colors.primary;
    return colors.green;
  };

  const data = fullResults || results;
  const benchmark = data.industry_benchmark || { avg: 2.6, top: 4.2 };
  const overallScore = data.overall_score || results.overall_score || 2.5;
  const maturityLevel = data.maturity_level?.name || 'Developing';
  const dimensions = data.dimension_scores || results.dimension_scores || [];

  // Helper function to find matching analysis for a dimension
  const findDimensionAnalysis = (dimName, dimId) => {
    const aiAnalysis = data.ai_analysis?.dimension_analysis || data.dimension_analysis || [];
    if (!Array.isArray(aiAnalysis) || aiAnalysis.length === 0) return null;
    
    return aiAnalysis.find(a => {
      if (!a) return false;
      if (dimId && (a.dimension_id === dimId || a.id === dimId)) return true;
      if (a.dimension === dimName || a.name === dimName) return true;
      const searchTerm = dimName.toLowerCase().split(' ')[0];
      const analysisName = (a.dimension || a.name || '').toLowerCase();
      return analysisName.includes(searchTerm) || searchTerm.includes(analysisName.split(' ')[0]);
    });
  };

  // BPCM-specific recommendations based on dimension and score
  const getBPCMSpecificRecommendation = (dimName, score, analysis) => {
    if (analysis) {
      const recs = analysis.quick_wins || analysis.recommendations || analysis.improvement_areas || [];
      if (Array.isArray(recs) && recs.length > 0) {
        return typeof recs[0] === 'string' ? recs[0] : recs[0].recommendation || recs[0].action || String(recs[0]);
      }
      if (analysis.current_state) {
        return analysis.current_state.substring(0, 150);
      }
    }
    
    const dimLower = dimName.toLowerCase();
    if (score < 2) {
      if (dimLower.includes('leadership') || dimLower.includes('sponsor')) 
        return 'CRITICAL: Secure executive sponsor with dedicated change leadership role and visible commitment';
      if (dimLower.includes('communication')) 
        return 'URGENT: Develop comprehensive communication strategy with multi-channel approach and feedback loops';
      if (dimLower.includes('stakeholder')) 
        return 'PRIORITY: Conduct stakeholder analysis and develop tailored engagement plans for each group';
      if (dimLower.includes('training') || dimLower.includes('capability') || dimLower.includes('learning')) 
        return 'IMMEDIATE: Assess skill gaps and implement targeted training programs with reinforcement';
      if (dimLower.includes('process') || dimLower.includes('methodology')) 
        return 'FOUNDATIONAL: Adopt structured change management methodology (e.g., ADKAR, Kotter)';
      if (dimLower.includes('culture') || dimLower.includes('readiness')) 
        return 'CRITICAL: Assess organizational readiness and address resistance through targeted interventions';
      if (dimLower.includes('measure') || dimLower.includes('metric') || dimLower.includes('kpi')) 
        return 'PRIORITY: Define change KPIs and implement adoption tracking mechanisms';
    } else if (score < 3) {
      if (dimLower.includes('leadership') || dimLower.includes('sponsor')) 
        return 'Expand change leadership network with champions at all organizational levels';
      if (dimLower.includes('communication')) 
        return 'Enhance communication with personalized messaging and two-way dialogue channels';
      if (dimLower.includes('stakeholder')) 
        return 'Deepen stakeholder engagement with co-creation workshops and feedback integration';
      if (dimLower.includes('training') || dimLower.includes('capability') || dimLower.includes('learning')) 
        return 'Scale training with blended learning approaches and on-the-job coaching';
      if (dimLower.includes('process') || dimLower.includes('methodology')) 
        return 'Standardize change processes with templates, playbooks, and governance frameworks';
      if (dimLower.includes('culture') || dimLower.includes('readiness')) 
        return 'Build change-positive culture with recognition programs and success celebrations';
      if (dimLower.includes('measure') || dimLower.includes('metric') || dimLower.includes('kpi')) 
        return 'Implement real-time adoption dashboards with predictive analytics';
    } else {
      if (dimLower.includes('leadership') || dimLower.includes('sponsor')) 
        return 'Achieve change leadership excellence with distributed ownership and self-sustaining sponsorship';
      if (dimLower.includes('communication')) 
        return 'Pioneer AI-powered personalized change communication at scale';
      if (dimLower.includes('stakeholder')) 
        return 'Implement continuous stakeholder sensing with predictive engagement models';
      if (dimLower.includes('training') || dimLower.includes('capability') || dimLower.includes('learning')) 
        return 'Achieve learning organization status with embedded continuous development';
      if (dimLower.includes('process') || dimLower.includes('methodology')) 
        return 'Lead industry in agile change practices with continuous improvement culture';
      if (dimLower.includes('culture') || dimLower.includes('readiness')) 
        return 'Achieve change-agile organization with high adaptability and resilience';
      if (dimLower.includes('measure') || dimLower.includes('metric') || dimLower.includes('kpi')) 
        return 'Pioneer change ROI measurement with value realization tracking';
    }
    return `Strengthen ${dimName.toLowerCase()} capabilities with targeted change management improvements`;
  };

  // Score-based roadmap phases
  const getBPCMRoadmap = (score) => {
    if (score < 2) {
      return {
        phase_1: {
          name: 'Foundation',
          focus_areas: ['Executive sponsorship', 'Change methodology', 'Baseline assessment'],
          key_initiatives: ['Secure executive sponsor with clear mandate', 'Select and adopt change framework', 'Conduct change readiness assessment', 'Build core change team'],
          investment_range: '$75K - $150K'
        },
        phase_2: {
          name: 'Capability Building',
          focus_areas: ['Communication infrastructure', 'Training foundation', 'Stakeholder mapping'],
          key_initiatives: ['Establish communication channels', 'Develop training curriculum', 'Complete stakeholder analysis', 'Create change champion network'],
          investment_range: '$100K - $200K'
        },
        phase_3: {
          name: 'Process Development',
          focus_areas: ['Methodology deployment', 'Tools implementation', 'Governance setup'],
          key_initiatives: ['Deploy change playbooks', 'Implement change management tools', 'Establish governance model', 'Launch pilot change initiatives'],
          investment_range: '$150K - $275K'
        },
        phase_4: {
          name: 'Integration',
          focus_areas: ['Project integration', 'Metrics establishment', 'Continuous improvement'],
          key_initiatives: ['Integrate with project management', 'Implement adoption metrics', 'Establish feedback loops', 'Document lessons learned'],
          investment_range: '$100K - $175K'
        }
      };
    } else if (score < 3) {
      return {
        phase_1: {
          name: 'Enhancement',
          focus_areas: ['Leadership expansion', 'Communication optimization', 'Process standardization'],
          key_initiatives: ['Expand change leadership network', 'Enhance communication personalization', 'Standardize change processes', 'Scale training programs'],
          investment_range: '$100K - $200K'
        },
        phase_2: {
          name: 'Scale-Up',
          focus_areas: ['Enterprise deployment', 'Tool enhancement', 'Culture building'],
          key_initiatives: ['Deploy change management enterprise-wide', 'Implement advanced analytics', 'Build change-positive culture', 'Develop internal certification'],
          investment_range: '$150K - $300K'
        },
        phase_3: {
          name: 'Optimization',
          focus_areas: ['Agile integration', 'Predictive capabilities', 'Value measurement'],
          key_initiatives: ['Integrate with agile methodologies', 'Implement predictive adoption models', 'Establish ROI measurement', 'Create center of excellence'],
          investment_range: '$175K - $350K'
        },
        phase_4: {
          name: 'Excellence',
          focus_areas: ['Best practice leadership', 'Innovation', 'Industry recognition'],
          key_initiatives: ['Pioneer change management innovations', 'Lead industry forums', 'Achieve change maturity certification', 'Build external partnerships'],
          investment_range: '$125K - $225K'
        }
      };
    }
    return {
      phase_1: {
        name: 'Leadership',
        focus_areas: ['AI-powered change', 'Predictive analytics', 'Self-sustaining model'],
        key_initiatives: ['Deploy AI-driven change insights', 'Achieve predictive change management', 'Build self-sustaining sponsorship', 'Pioneer industry innovations'],
        investment_range: '$125K - $250K'
      },
      phase_2: {
        name: 'Innovation',
        focus_areas: ['Emerging technologies', 'Continuous learning', 'Ecosystem building'],
        key_initiatives: ['Integrate emerging technologies', 'Achieve learning organization status', 'Build change ecosystem', 'Lead thought leadership'],
        investment_range: '$175K - $350K'
      },
      phase_3: {
        name: 'Transformation',
        focus_areas: ['Organizational agility', 'Digital-first change', 'Value maximization'],
        key_initiatives: ['Achieve change-agile organization', 'Implement digital-first approach', 'Maximize change value realization', 'Pioneer new methodologies'],
        investment_range: '$200K - $400K'
      },
      phase_4: {
        name: 'Legacy Building',
        focus_areas: ['Industry transformation', 'Sustainable practices', 'Knowledge transfer'],
        key_initiatives: ['Transform industry standards', 'Build sustainable change culture', 'Share knowledge externally', 'Establish lasting partnerships'],
        investment_range: '$150K - $300K'
      }
    };
  };

  // Slide 1: Title
  const slide1 = pptx.addSlide();
  slide1.background = { color: colors.dark };
  
  slide1.addText('SAMANVAI', {
    x: 0.5, y: 0.4, w: 3, h: 0.5,
    fontSize: 24, bold: true, color: colors.primary
  });
  
  slide1.addText('Business Process Change Management', {
    x: 0.5, y: 1.8, w: 7, h: 0.8,
    fontSize: 40, bold: true, color: colors.white
  });
  
  slide1.addText('Maturity Assessment Report', {
    x: 0.5, y: 2.6, w: 7, h: 0.6,
    fontSize: 28, color: colors.secondary
  });
  
  slide1.addText(data.company_name || results.company_name, {
    x: 0.5, y: 3.5, w: 6, h: 0.6,
    fontSize: 24, color: colors.white
  });
  
  slide1.addText(`${data.industry || results.industry} | ${data.company_size || results.company_size}`, {
    x: 0.5, y: 4.1, w: 6, h: 0.4,
    fontSize: 12, color: 'A0AEC0'
  });
  
  // Score badge
  slide1.addShape('rect', {
    x: 7.3, y: 1.6, w: 2.2, h: 2.8,
    fill: { color: getScoreColor(overallScore) },
    line: { color: colors.white, width: 2 }
  });
  slide1.addText(overallScore.toFixed(1), {
    x: 7.3, y: 1.9, w: 2.2, h: 1,
    fontSize: 48, bold: true, color: colors.white, align: 'center'
  });
  slide1.addText('out of 5.0', {
    x: 7.3, y: 2.8, w: 2.2, h: 0.3,
    fontSize: 11, color: colors.white, align: 'center'
  });
  slide1.addText(maturityLevel, {
    x: 7.3, y: 3.3, w: 2.2, h: 0.4,
    fontSize: 12, bold: true, color: colors.white, align: 'center'
  });

  // Slide 2: Executive Summary
  const slide2 = pptx.addSlide();
  slide2.addShape('rect', { x: 0, y: 0, w: 10, h: 1, fill: { color: colors.primary } });
  slide2.addText('Executive Summary', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Stats row
  const stats = [
    { label: 'Your Score', value: overallScore.toFixed(1), color: getScoreColor(overallScore) },
    { label: 'Industry Avg', value: String(benchmark.avg), color: colors.mediumGray },
    { label: 'Top Performers', value: String(benchmark.top), color: colors.green },
    { label: 'Gap to Top', value: (benchmark.top - overallScore).toFixed(1), color: colors.secondary }
  ];
  
  stats.forEach((stat, idx) => {
    const x = 0.5 + idx * 2.4;
    slide2.addShape('rect', { x, y: 1.3, w: 2.2, h: 1.1, fill: { color: colors.lightGray } });
    slide2.addText(stat.label, { x, y: 1.4, w: 2.2, h: 0.3, fontSize: 9, color: colors.mediumGray, align: 'center' });
    slide2.addText(stat.value, { x, y: 1.65, w: 2.2, h: 0.6, fontSize: 24, bold: true, color: stat.color, align: 'center' });
  });
  
  // Summary text
  let summaryText = data.ai_analysis?.executive_summary || 
    `${data.company_name} has achieved a BPCM Maturity score of ${overallScore.toFixed(1)}/5.0, placing the organization at the "${maturityLevel}" level.`;
  
  if (typeof summaryText === 'string') {
    summaryText = summaryText.substring(0, 1200);
  }
  
  slide2.addText(summaryText, {
    x: 0.5, y: 2.6, w: 9, h: 2.8,
    fontSize: 11, color: colors.darkGray, valign: 'top'
  });

  // Slide 3: 7 BPCM Levers with Detailed Analysis
  const slide3 = pptx.addSlide();
  slide3.addShape('rect', { x: 0, y: 0, w: 10, h: 1, fill: { color: colors.secondary } });
  slide3.addText('7 Change Management Levers Analysis', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 26, bold: true, color: colors.white
  });
  
  dimensions.slice(0, 7).forEach((dim, idx) => {
    const y = 1.2 + idx * 0.7;
    const dimScore = dim.score || 2.5;
    
    slide3.addShape('rect', {
      x: 0.5, y, w: 0.8, h: 0.55,
      fill: { color: getScoreColor(dimScore) }
    });
    slide3.addText(dimScore.toFixed(1), {
      x: 0.5, y: y + 0.1, w: 0.8, h: 0.35,
      fontSize: 14, bold: true, color: colors.white, align: 'center'
    });
    
    slide3.addText(dim.dimension_name || `Lever ${idx + 1}`, {
      x: 1.5, y, w: 4, h: 0.35,
      fontSize: 11, bold: true, color: colors.darkGray
    });
    
    // Progress bar
    slide3.addShape('rect', { x: 5.5, y: y + 0.15, w: 4, h: 0.25, fill: { color: colors.lightGray } });
    slide3.addShape('rect', { x: 5.5, y: y + 0.15, w: 4 * (dimScore / 5), h: 0.25, fill: { color: getScoreColor(dimScore) } });
  });

  // Slide 3b: Detailed Dimension Recommendations
  const slide3b = pptx.addSlide();
  slide3b.addShape('rect', { x: 0, y: 0, w: 10, h: 1, fill: { color: colors.accent } });
  slide3b.addText('Lever-Specific Recommendations', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 26, bold: true, color: colors.white
  });
  
  dimensions.slice(0, 5).forEach((dim, idx) => {
    const y = 1.2 + idx * 1.0;
    const dimScore = dim.score || 2.5;
    const dimName = dim.dimension_name || `Lever ${idx + 1}`;
    const dimId = dim.dimension_id || dim.id;
    const analysis = findDimensionAnalysis(dimName, dimId);
    
    // Dimension header
    slide3b.addText(`${dimName}: ${dimScore.toFixed(1)}/5.0`, {
      x: 0.5, y, w: 4.5, h: 0.35,
      fontSize: 11, bold: true, color: colors.darkGray
    });
    
    // Score bar
    slide3b.addShape('rect', { x: 5.5, y: y + 0.05, w: 4, h: 0.25, fill: { color: colors.lightGray } });
    slide3b.addShape('rect', { x: 5.5, y: y + 0.05, w: (dimScore / 5) * 4, h: 0.25, fill: { color: getScoreColor(dimScore) } });
    
    // Specific recommendation
    const keyRec = getBPCMSpecificRecommendation(dimName, dimScore, analysis);
    slide3b.addText(`→ ${keyRec}`, {
      x: 0.5, y: y + 0.4, w: 9, h: 0.5,
      fontSize: 9, color: colors.mediumGray, wrap: true
    });
  });

  // Slide 4: Areas of Concern
  const slide4 = pptx.addSlide();
  slide4.addShape('rect', { x: 0, y: 0, w: 10, h: 1, fill: { color: colors.red } });
  slide4.addText('Areas of Concern', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const concerns = data.ai_analysis?.areas_of_concern || {};
  
  // Generate default critical concerns based on lowest scoring dimensions
  const getDefaultCriticalAreas = () => {
    if (!dimensions || dimensions.length === 0) return [];
    const sortedDims = [...dimensions].sort((a, b) => (a.score || 0) - (b.score || 0));
    return sortedDims.slice(0, 3).map(dim => {
      const name = dim.dimension_name || dim.name;
      const score = dim.score || 0;
      if (score < 1.5) return `CRITICAL: ${name} requires immediate intervention - foundational capabilities missing`;
      if (score < 2.5) return `HIGH PRIORITY: ${name} needs focused improvement to enable effective change management`;
      return `MONITOR: ${name} should be enhanced to achieve change management excellence`;
    });
  };
  
  // Default high priority based on score
  const defaultHighPriority = overallScore < 2 
    ? ['Establish executive sponsorship with clear mandate and visible commitment', 'Develop structured change management methodology and governance', 'Build change management capability with training and tools']
    : overallScore < 3
    ? ['Expand change champion network across business units', 'Implement predictive adoption analytics and dashboards', 'Scale training programs with blended learning approaches']
    : ['Pioneer AI-enabled change management insights', 'Achieve industry-leading change adoption rates', 'Build self-sustaining change culture'];
  
  // Critical
  const criticalItems = concerns.critical?.length > 0 ? concerns.critical : getDefaultCriticalAreas();
  
  slide4.addText('🔴 Critical (Immediate Action Required)', {
    x: 0.5, y: 1.2, w: 9, h: 0.4,
    fontSize: 12, bold: true, color: colors.red
  });
  
  criticalItems.slice(0, 3).forEach((item, idx) => {
    const itemText = typeof item === 'string' ? item : item.description || item.area || String(item);
    slide4.addText(`• ${itemText}`, {
      x: 0.7, y: 1.65 + idx * 0.35, w: 8.5, h: 0.35,
      fontSize: 10, color: colors.darkGray
    });
  });
  
  // High Priority
  const highItems = concerns.high_priority?.length > 0 ? concerns.high_priority : defaultHighPriority;
  
  slide4.addText('🟠 High Priority (Action within 3 months)', {
    x: 0.5, y: 2.9, w: 9, h: 0.4,
    fontSize: 12, bold: true, color: colors.amber
  });
  
  highItems.slice(0, 3).forEach((item, idx) => {
    const itemText = typeof item === 'string' ? item : item.description || item.area || String(item);
    slide4.addText(`• ${itemText}`, {
      x: 0.7, y: 3.35 + idx * 0.35, w: 8.5, h: 0.35,
      fontSize: 10, color: colors.darkGray
    });
  });

  // Slide 5: 12-Month Roadmap
  const slide5 = pptx.addSlide();
  slide5.addShape('rect', { x: 0, y: 0, w: 10, h: 1, fill: { color: colors.accent } });
  slide5.addText('12-Month Strategic Roadmap', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 26, bold: true, color: colors.white
  });
  
  const aiRoadmap = data.ai_analysis?.strategic_roadmap || {};
  const defaultRoadmap = getBPCMRoadmap(overallScore);
  
  const phases = [
    { key: 'phase_1', months: 'Months 1-3', color: colors.primary },
    { key: 'phase_2', months: 'Months 4-6', color: colors.secondary },
    { key: 'phase_3', months: 'Months 7-9', color: colors.accent },
    { key: 'phase_4', months: 'Months 10-12', color: colors.green }
  ];
  
  phases.forEach((phase, idx) => {
    const x = 0.5 + idx * 2.4;
    const phaseData = aiRoadmap[phase.key] || defaultRoadmap[phase.key] || {};
    
    // Phase header
    slide5.addShape('rect', { x, y: 1.2, w: 2.2, h: 0.8, fill: { color: phase.color } });
    slide5.addText(phaseData.name || `Phase ${idx + 1}`, { 
      x, y: 1.35, w: 2.2, h: 0.3, 
      fontSize: 12, bold: true, color: colors.white, align: 'center' 
    });
    slide5.addText(phase.months, { 
      x, y: 1.65, w: 2.2, h: 0.25, 
      fontSize: 9, color: colors.white, align: 'center' 
    });
    
    // Content box
    slide5.addShape('rect', { x, y: 2.05, w: 2.2, h: 2.8, fill: { color: colors.lightGray } });
    
    let contentY = 2.15;
    
    // Focus areas
    const focusAreas = phaseData.focus_areas || [];
    if (focusAreas.length > 0) {
      slide5.addText('Focus:', { 
        x: x + 0.1, y: contentY, w: 2, h: 0.25, 
        fontSize: 8, bold: true, color: phase.color 
      });
      contentY += 0.25;
      
      focusAreas.slice(0, 2).forEach(area => {
        const areaText = typeof area === 'string' ? area : (area.area || area.name || String(area));
        slide5.addText(`• ${areaText}`, {
          x: x + 0.1, y: contentY, w: 2, h: 0.35,
          fontSize: 7, color: colors.darkGray, wrap: true
        });
        contentY += 0.32;
      });
    }
    
    contentY += 0.1;
    
    // Key initiatives
    const initiatives = phaseData.key_initiatives || phaseData.objectives || [];
    if (initiatives.length > 0) {
      slide5.addText('Initiatives:', { 
        x: x + 0.1, y: contentY, w: 2, h: 0.25, 
        fontSize: 8, bold: true, color: phase.color 
      });
      contentY += 0.25;
      
      initiatives.slice(0, 3).forEach((item) => {
        const itemText = typeof item === 'string' ? item : (item.name || item.initiative || String(item));
        const truncatedText = itemText.length > 40 ? itemText.substring(0, 37) + '...' : itemText;
        slide5.addText(`• ${truncatedText}`, {
          x: x + 0.1, y: contentY, w: 2, h: 0.4,
          fontSize: 7, color: colors.mediumGray, valign: 'top', wrap: true
        });
        contentY += 0.38;
      });
    }
    
    // Investment
    if (phaseData.investment_range) {
      slide5.addShape('rect', { x: x + 0.1, y: 4.45, w: 2, h: 0.3, fill: { color: phase.color } });
      slide5.addText(phaseData.investment_range, {
        x: x + 0.1, y: 4.48, w: 2, h: 0.25,
        fontSize: 8, bold: true, color: colors.white, align: 'center'
      });
    }
    
    // Arrow between phases
    if (idx < 3) {
      slide5.addText('→', {
        x: x + 2.1, y: 1.5, w: 0.4, h: 0.4,
        fontSize: 18, color: colors.mediumGray, align: 'center'
      });
    }
  });

  // Slide 6: ROI Analysis
  const slide6 = pptx.addSlide();
  slide6.addShape('rect', { x: 0, y: 0, w: 10, h: 1, fill: { color: colors.green } });
  slide6.addText('ROI Analysis & Business Value', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 26, bold: true, color: colors.white
  });
  
  const roi = data.ai_analysis?.roi_analysis || {};
  
  const roiCards = [
    { label: 'Total Investment', value: roi.total_investment || 'Not available', color: colors.primary },
    { label: 'Expected ROI', value: roi.expected_roi || 'Not available', color: colors.green },
    { label: 'Payback Period', value: '12-18 months', color: colors.secondary },
    { label: 'Risk Reduction', value: '40-60%', color: colors.accent }
  ];
  
  roiCards.forEach((card, idx) => {
    const x = 0.5 + idx * 2.4;
    slide6.addShape('rect', { x, y: 1.3, w: 2.2, h: 1.2, fill: { color: card.color } });
    slide6.addText(card.label, { x, y: 1.4, w: 2.2, h: 0.3, fontSize: 9, color: colors.white, align: 'center' });
    slide6.addText(card.value, { x, y: 1.7, w: 2.2, h: 0.6, fontSize: 16, bold: true, color: colors.white, align: 'center' });
  });
  
  // Business value categories
  slide6.addText('Business Value Realization', {
    x: 0.5, y: 2.8, w: 9, h: 0.4,
    fontSize: 14, bold: true, color: colors.darkGray
  });
  
  const valueCategories = [
    { title: 'Change Success Rate', benefits: ['Higher adoption rates', 'Reduced resistance', 'Faster time-to-value'] },
    { title: 'Cost Efficiency', benefits: ['Lower rework costs', 'Reduced support needs', 'Optimized training'] },
    { title: 'Risk Management', benefits: ['Fewer failed changes', 'Better compliance', 'Reduced disruption'] },
    { title: 'Organizational Agility', benefits: ['Faster transformation', 'Better adaptability', 'Continuous improvement'] }
  ];
  
  valueCategories.forEach((cat, idx) => {
    const x = 0.5 + idx * 2.4;
    slide6.addShape('rect', { x, y: 3.3, w: 2.2, h: 1.8, fill: { color: colors.lightGray } });
    slide6.addText(cat.title, { x: x + 0.1, y: 3.4, w: 2, h: 0.35, fontSize: 9, bold: true, color: colors.primary });
    slide6.addText(cat.benefits.map(b => `• ${b}`).join('\n'), {
      x: x + 0.1, y: 3.75, w: 2, h: 1.2,
      fontSize: 8, color: colors.darkGray, valign: 'top'
    });
  });

  // Slide 7: Case Studies
  const slide7 = pptx.addSlide();
  slide7.addShape('rect', { x: 0, y: 0, w: 10, h: 1, fill: { color: colors.secondary } });
  slide7.addText('Relevant Case Studies', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 26, bold: true, color: colors.white
  });
  
  const caseStudies = data.case_studies || [];
  
  caseStudies.slice(0, 2).forEach((cs, idx) => {
    const y = 1.2 + idx * 2.2;
    
    slide7.addShape('rect', { x: 0.5, y, w: 9, h: 2, fill: { color: colors.lightGray } });
    slide7.addText(cs.title || `Case Study ${idx + 1}`, {
      x: 0.7, y: y + 0.1, w: 8.6, h: 0.4,
      fontSize: 12, bold: true, color: colors.primary
    });
    slide7.addText(cs.challenge || 'Challenge description', {
      x: 0.7, y: y + 0.5, w: 8.6, h: 0.4,
      fontSize: 9, color: colors.darkGray
    });
    
    // Results
    const resultsText = (cs.results || []).slice(0, 3).map(r => `• ${r}`).join('\n');
    slide7.addText('Key Results:\n' + resultsText, {
      x: 0.7, y: y + 1, w: 5, h: 0.9,
      fontSize: 8, color: colors.mediumGray
    });
    
    if (cs.roi) {
      slide7.addText(`ROI: ${cs.roi}`, {
        x: 6, y: y + 1.4, w: 3, h: 0.3,
        fontSize: 10, bold: true, color: colors.green
      });
    }
  });

  // ============== Slide 8: Level Progression - Journey to Next BPCM Level ==============
  const levelProgression = data.ai_analysis?.level_progression;
  if (levelProgression) {
    const slideLP = pptx.addSlide();
    
    // Header
    slideLP.addShape('rect', {
      x: 0, y: 0, w: 10, h: 1.1,
      fill: { color: colors.primary }
    });
    slideLP.addText('Level Up: Journey to Next BPCM Maturity Level', {
      x: 0.5, y: 0.2, w: 9, h: 0.4,
      fontSize: 24, bold: true, color: colors.white
    });
    slideLP.addText('Time, Effort, Investment & Benefits Analysis', {
      x: 0.5, y: 0.6, w: 9, h: 0.3,
      fontSize: 11, color: 'FFFFFF'
    });

    // Current to Next Level Visual
    const currentLevel = levelProgression.current_level?.level_number || Math.floor(overallScore);
    const nextLevel = levelProgression.next_level?.level_number || Math.min(currentLevel + 1, 5);
    const currentLevelName = levelProgression.current_level?.level_name || maturityLevel || 'Current';
    const nextLevelName = levelProgression.next_level?.level_name || 'Next Level';
    
    // Current Level Circle
    slideLP.addShape('ellipse', {
      x: 1.5, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.amber }
    });
    slideLP.addText(String(currentLevel), {
      x: 1.5, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Current', {
      x: 1.2, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(currentLevelName, {
      x: 1, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.mediumGray, align: 'center'
    });

    // Arrow with time
    slideLP.addShape('rect', {
      x: 3.2, y: 1.75, w: 3.6, h: 0.3,
      fill: { color: colors.primary }
    });
    slideLP.addText(levelProgression.progression_requirements?.time_estimate || '6-12 months', {
      x: 3.2, y: 2.1, w: 3.6, h: 0.3,
      fontSize: 10, bold: true, color: colors.primary, align: 'center'
    });

    // Next Level Circle
    slideLP.addShape('ellipse', {
      x: 7.3, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.primary }
    });
    slideLP.addText(String(nextLevel), {
      x: 7.3, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Target', {
      x: 7, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(nextLevelName, {
      x: 6.8, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.primary, align: 'center'
    });

    // Resource Requirements Grid
    const reqY = 3.2;
    const reqCards = [
      { label: 'TIME', value: levelProgression.progression_requirements?.time_estimate || '6-12 months', color: '3B82F6' },
      { label: 'MANPOWER', value: levelProgression.progression_requirements?.manpower_resources?.internal_fte || 'Not available', color: '8B5CF6' },
      { label: 'INVESTMENT', value: levelProgression.progression_requirements?.investment_required?.total_range || 'Not available', color: colors.primary },
      { label: 'ROI', value: levelProgression.progression_requirements?.business_benefits?.quantified_value || 'Not available', color: colors.amber }
    ];

    reqCards.forEach((card, idx) => {
      const x = 0.5 + idx * 2.4;
      
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 1.2,
        fill: { color: colors.lightGray }
      });
      
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 0.35,
        fill: { color: card.color }
      });
      slideLP.addText(card.label, {
        x: x, y: reqY + 0.05, w: 2.2, h: 0.25,
        fontSize: 9, bold: true, color: colors.white, align: 'center'
      });
      
      slideLP.addText(card.value, {
        x: x + 0.1, y: reqY + 0.45, w: 2, h: 0.65,
        fontSize: 11, bold: true, color: card.color, align: 'center', valign: 'middle'
      });
    });

    // Business Benefits Section
    const benY = 4.6;
    slideLP.addText('Change Management Benefits at Next Level', {
      x: 0.5, y: benY, w: 9, h: 0.35,
      fontSize: 12, bold: true, color: colors.primary
    });

    const benefits = [
      { label: 'Change Success Rate', value: levelProgression.progression_requirements?.business_benefits?.efficiency_gains || 'Not available' },
      { label: 'Adoption Speed', value: levelProgression.progression_requirements?.business_benefits?.revenue_impact || 'Not available' },
      { label: 'Resistance Reduction', value: levelProgression.progression_requirements?.business_benefits?.risk_reduction || 'Not available' }
    ];

    benefits.forEach((ben, idx) => {
      const x = 0.5 + idx * 3.1;
      slideLP.addShape('rect', {
        x: x, y: benY + 0.4, w: 2.9, h: 0.7,
        fill: { color: 'EFF6FF' },
        line: { color: colors.primary, width: 1 }
      });
      slideLP.addText(ben.label, {
        x: x + 0.1, y: benY + 0.45, w: 2.7, h: 0.25,
        fontSize: 8, color: colors.mediumGray
      });
      slideLP.addText(ben.value, {
        x: x + 0.1, y: benY + 0.7, w: 2.7, h: 0.35,
        fontSize: 10, bold: true, color: colors.primary
      });
    });
  }

  // ============== Slide 9 (or 8 if no level progression): Thank You ==============
  const slideThankYou = pptx.addSlide();
  slideThankYou.background = { color: colors.dark };
  
  slideThankYou.addText('SAMANVAI', {
    x: 0.5, y: 0.4, w: 3, h: 0.5,
    fontSize: 24, bold: true, color: colors.primary
  });
  
  slideThankYou.addText('Thank You', {
    x: 0.5, y: 2, w: 9, h: 1,
    fontSize: 44, bold: true, color: colors.white, align: 'center'
  });
  
  slideThankYou.addText('Your Change Management Journey Starts Here', {
    x: 0.5, y: 3, w: 9, h: 0.5,
    fontSize: 18, color: colors.secondary, align: 'center'
  });
  
  slideThankYou.addText('www.samanvai.com', {
    x: 0.5, y: 4.3, w: 9, h: 0.5,
    fontSize: 18, color: colors.primary, align: 'center'
  });

  // Download
  const companyName = (data.company_name || results.company_name || 'Company').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const fileName = `BPCM_Assessment_${companyName}_${new Date().toISOString().split('T')[0]}`;
  appendQualitySlide(pptx, fullResults || results);
  return pptx.writeFile({ fileName });
};

export default generateBPCMPptx;
